import format from 'date-fns/format'

import type { IBestFit, IEntityAppointment } from './typings'
import type { IScheduleSettingsDetail } from '../closingRequirements'
import type {
  IAppointmentInfo,
  CardClosingRequirements,
} from '@sales-center/common'

export function formatWorkTime(
  workTime: Date | null,
): [date: string, time: string, dateText?: string] {
  if (workTime === null) return ['', '']

  const dateText = format(workTime, 'EEEE')
  const date = format(workTime, 'MM/dd/yyyy')
  const time = format(workTime, 'hh:mm aa')

  return [date, time, dateText]
}

export function formatBestFit(bestFit: IBestFit) {
  const { resultId, workTime } = bestFit

  const [date, formattedTime, dateText] = formatWorkTime(workTime)

  const timeRange = formatTimeRange(bestFit.timeRange)

  const { buttonLabel, time } =
    timeRange === null
      ? { buttonLabel: formattedTime, time: undefined }
      : { buttonLabel: timeRange, time: formattedTime }

  return {
    date,
    dateText,
    time,
    resultId,
    buttonLabel,
  }
}

// feels like a hack, we should get PP to provide the format we want
export function formatTimeRange(timeRange: string | null) {
  if (timeRange === null || timeRange.trim() === '' || timeRange.trim() === '-')
    return null

  let [start, end] = timeRange.split(' - ')
  if (!start || !end) return timeRange

  if (start.length != 8) start = `0${start}`
  if (end.length != 8) end = `0${end}`

  return `${start} - ${end}`
}

export function formatTimeForSearch(date: Date | undefined) {
  if (!date) return

  return format(date, "u-MM-dd'T'HH:mm:ss")
}

export function apptTypeToTitle(appt: IEntityAppointment): string {
  const { appointmentType, billingType } = appt

  switch (appointmentType) {
    case 'Estimate':
      return `${billingType} ${appointmentType}`
    case 'Initial':
      return 'Service Setup'
    case 'OneTime':
      return 'Service Order'
  }
}

export function checkSchedulingComplete(
  services: IAppointmentInfo[],
  reqs: CardClosingRequirements,
) {
  const {
    serviceScheduleRequired: svcReq,
    freeEstimateScheduleRequired: freeReq,
    paidEstimateScheduleRequired: paidReq,
  } = reqs

  const svc = new Set<string>()
  const free = new Set<string>()
  const paid = new Set<string>()

  services.map((appt) => {
    const { appointmentId, estimateId, billingType } = appt

    if (appointmentId !== null) svc.add(appointmentId)
    if (estimateId !== null) {
      if (billingType === 'Free') free.add(estimateId)
      if (billingType === 'Paid') paid.add(estimateId)
    }
  })

  const svcComplete = svcReq ? svc.size > 0 : true
  const freeComplete = freeReq ? free.size > 0 : true
  const paidComplete = paidReq ? paid.size > 0 : true

  const nonCancelledCount = free.size + paid.size + svc.size

  return freeComplete && paidComplete && svcComplete && nonCancelledCount > 0
}

export function checkSchedulingVisibility(settings?: IScheduleSettingsDetail) {
  if (!settings) return false

  const {
    scheduleVisibilitySettings: {
      serviceScheduleEnabled: svc,
      freeEstimateScheduleEnabled: free,
      paidEstimateScheduleEnabled: paid,
    },
  } = settings

  return svc || free || paid
}
